<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import FinestCard from '@/components/FinestCard.vue'
import ValueDisplay from '@/components/ValueDisplay.vue'
import EmptyState from '@/components/EmptyState.vue'
import PolicyList, { type PolicyRow } from '@/pages/banks/my-bank/PolicyList.vue'
import PartnerBankData = App.Data.PartnerBank.PartnerBankData

const props = defineProps<{
  partnerBank: PartnerBankData
}>()

const { t } = useI18n()

const label = (namespace: string, value: string): string => {
  const key = `${namespace}.${value}`
  const translated = t(key)
  return translated === key ? value : translated
}

const businessTypeRows = computed<PolicyRow[]>(() =>
  (props.partnerBank.business_type_policies ?? []).map((policy) => ({
    label: label('businessType', policy.business_type),
    decision: policy.decision,
    notes: policy.notes,
  })),
)

const customerTypeRows = computed<PolicyRow[]>(() =>
  (props.partnerBank.customer_type_policies ?? []).map((policy) => ({
    label: label('customerType', policy.customer_type),
    decision: policy.decision,
    notes: policy.notes,
  })),
)

const loanProductRows = computed<PolicyRow[]>(() =>
  (props.partnerBank.loan_product_policies ?? []).map((policy) => ({
    label: label('loanProductType', policy.loan_product_type),
    decision: policy.decision,
    notes: policy.notes,
  })),
)

const objectTypeRows = computed<PolicyRow[]>(() =>
  (props.partnerBank.object_type_policies ?? []).map((policy) => ({
    label: label('objectType', policy.object_type),
    decision: policy.decision,
    notes: policy.notes,
    suffix: policy.decision !== 'no' && policy.max_ltv_percent ? t('max_ltv', { value: policy.max_ltv_percent }) : null,
  })),
)

const coverageRules = computed(() => props.partnerBank.coverage_rules ?? [])
const excludedRegions = computed(() => props.partnerBank.excluded_regions ?? [])
const industryPolicies = computed(() => props.partnerBank.industry_policies ?? [])
const objectRule = computed(() => props.partnerBank.object_rule ?? null)

const objectRuleAcceptances = computed<PolicyRow[]>(() => {
  const rule = objectRule.value
  if (!rule) return []
  return [
    { label: t('accepts_weg'), decision: rule.accepts_weg },
    { label: t('accepts_leasehold_properties'), decision: rule.accepts_leasehold_properties },
    { label: t('accepts_mining_damage_waiver'), decision: rule.accepts_mining_damage_waiver },
    {
      label: t('accepts_renovation_instead_of_min_energy_level'),
      decision: rule.accepts_renovation_instead_of_min_energy_level,
    },
    { label: t('accepts_pending_energy_certificate'), decision: rule.accepts_pending_energy_certificate },
  ]
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <FinestCard :title="t('business_types')" icon="pi pi-briefcase">
        <PolicyList v-if="businessTypeRows.length" :items="businessTypeRows" />
        <EmptyState v-else :description="t('no_entries')" />
      </FinestCard>

      <FinestCard :title="t('customer_types')" icon="pi pi-users">
        <PolicyList v-if="customerTypeRows.length" :items="customerTypeRows" />
        <EmptyState v-else :description="t('no_entries')" />
      </FinestCard>

      <FinestCard :title="t('loan_products')" icon="pi pi-wallet">
        <PolicyList v-if="loanProductRows.length" :items="loanProductRows" />
        <EmptyState v-else :description="t('no_entries')" />
      </FinestCard>

      <FinestCard :title="t('object_types')" icon="pi pi-home">
        <PolicyList v-if="objectTypeRows.length" :items="objectTypeRows" />
        <EmptyState v-else :description="t('no_entries')" />
      </FinestCard>
    </div>

    <FinestCard :title="t('industries')" icon="pi pi-sitemap">
      <ul v-if="industryPolicies.length" class="divide-surface-100 divide-y">
        <li
          v-for="policy in industryPolicies"
          :key="policy.id ?? policy.industry_name"
          class="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
          <div class="flex items-center justify-between gap-4">
            <div class="flex flex-col">
              <span class="text-surface-800 font-medium">{{ policy.industry_name }}</span>
              <span class="text-surface-400 text-xs">{{ t(`borrowerKind.${policy.borrower_kind}`) }}</span>
            </div>
            <Tag
              :value="t(`industryStatus.${policy.status}`)"
              :severity="policy.status === 'excluded' ? 'danger' : 'warn'"
              class="w-fit" />
          </div>
          <p v-if="policy.notes" class="text-surface-500 text-sm">{{ policy.notes }}</p>
        </li>
      </ul>
      <EmptyState v-else :description="t('no_entries')" />
    </FinestCard>

    <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <FinestCard :title="t('coverage_rules')" icon="pi pi-map">
        <ul v-if="coverageRules.length" class="divide-surface-100 divide-y">
          <li
            v-for="rule in coverageRules"
            :key="rule.id ?? `${rule.borrower_kind}-${rule.coverage_type}`"
            class="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
            <div class="flex items-center justify-between gap-4">
              <span class="text-surface-800 font-medium">{{
                rule.region_text || t(`coverageType.${rule.coverage_type}`)
              }}</span>
              <Tag :value="t(`borrowerKind.${rule.borrower_kind}`)" severity="secondary" class="w-fit" />
            </div>
            <span class="text-surface-500 text-sm">
              {{ t(`coverageType.${rule.coverage_type}`) }}
              <template v-if="rule.coverage_type === 'radius' && rule.radius_km"> · {{ rule.radius_km }} km </template>
            </span>
          </li>
        </ul>
        <EmptyState v-else :description="t('no_entries')" />
      </FinestCard>

      <FinestCard :title="t('excluded_regions')" icon="pi pi-ban">
        <ul v-if="excludedRegions.length" class="divide-surface-100 divide-y">
          <li
            v-for="(region, index) in excludedRegions"
            :key="region.id ?? index"
            class="flex flex-col gap-1 py-3 first:pt-0 last:pb-0">
            <div class="flex items-center justify-between gap-4">
              <span class="text-surface-800 font-medium">
                {{ [region.postal_code, region.region_text].filter(Boolean).join(' · ') }}
              </span>
              <Tag :value="t(`borrowerKind.${region.borrower_kind}`)" severity="secondary" class="w-fit" />
            </div>
            <p v-if="region.notes" class="text-surface-500 text-sm">{{ region.notes }}</p>
          </li>
        </ul>
        <EmptyState v-else :description="t('no_entries')" />
      </FinestCard>
    </div>

    <FinestCard :title="t('object_rule')" icon="pi pi-cog">
      <template v-if="objectRule">
        <div class="grid gap-x-12 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
          <ValueDisplay
            :label="t('max_commercial_area_share_percent')"
            :value="objectRule.max_commercial_area_share_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('max_commercial_rent_share_percent')"
            :value="objectRule.max_commercial_rent_share_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('max_leasehold_ltv_percent')"
            :value="objectRule.max_leasehold_ltv_percent"
            format="number"
            suffix=" %" />
          <ValueDisplay
            :label="t('minimum_energy_certificate_level')"
            :value="objectRule.minimum_energy_certificate_level"
            format="string" />
        </div>
        <div class="mt-8">
          <PolicyList :items="objectRuleAcceptances" />
        </div>
        <p v-if="objectRule.notes" class="text-surface-500 mt-6 text-sm">{{ objectRule.notes }}</p>
      </template>
      <EmptyState v-else :description="t('no_entries')" />
    </FinestCard>
  </div>
</template>

<i18n>
{
  "de": {
    "no_entries": "Keine Einträge hinterlegt.",
    "business_types": "Geschäftsarten",
    "customer_types": "Kundentypen",
    "loan_products": "Darlehensprodukte",
    "object_types": "Objektarten",
    "industries": "Branchen",
    "coverage_rules": "Abdeckungsregeln",
    "excluded_regions": "Ausgeschlossene Regionen",
    "object_rule": "Objektregeln",
    "max_ltv": "max. {value} % LTV",
    "max_commercial_area_share_percent": "Max. Gewerbeflächenanteil",
    "max_commercial_rent_share_percent": "Max. Gewerbemietanteil",
    "max_leasehold_ltv_percent": "Max. LTV Erbbaurecht",
    "minimum_energy_certificate_level": "Mind. Energieausweisklasse",
    "accepts_weg": "WEG akzeptiert",
    "accepts_leasehold_properties": "Erbbaurecht akzeptiert",
    "accepts_mining_damage_waiver": "Bergschadenverzicht akzeptiert",
    "accepts_renovation_instead_of_min_energy_level": "Sanierung statt Mindest-Energieklasse",
    "accepts_pending_energy_certificate": "Ausstehender Energieausweis akzeptiert",
    "businessType": {
      "buy_and_hold": "Buy & Hold",
      "commercial_trade": "Gewerblicher Handel",
      "developer": "Bauträger",
      "subdivision": "Aufteilergeschäft"
    },
    "customerType": {
      "civil_servant": "Beamter",
      "employee": "Angestellter",
      "entrepreneur": "Unternehmer",
      "freelancer": "Freiberufler",
      "rentier": "Privatier"
    },
    "loanProductType": {
      "annuity": "Annuitätendarlehen",
      "building_society": "Bauspardarlehen",
      "bullet": "Endfälliges Darlehen",
      "non_recourse": "Non-Recourse",
      "subsidized": "Fördderdarlehen",
      "variable": "Variables Darlehen"
    },
    "objectType": {
      "residential": "Wohnen",
      "mixed_use": "Gemischte Nutzung",
      "office": "Büro",
      "commercial": "Gewerbe",
      "care_and_health": "Pflege & Gesundheit",
      "land": "Grundstück",
      "hotel": "Hotel",
      "logistics": "Logistik",
      "wholesale_and_retail": "Groß- & Einzelhandel",
      "other": "Sonstige"
    },
    "borrowerKind": {
      "natural_person": "Natürliche Person",
      "legal_entity": "Juristische Person"
    },
    "coverageType": {
      "radius": "Umkreis",
      "state": "Bundesland",
      "nationwide": "Bundesweit",
      "free_text": "Freitext"
    },
    "industryStatus": {
      "conditional": "Bedingt",
      "excluded": "Ausgeschlossen"
    }
  },
  "en": {
    "no_entries": "No entries set.",
    "business_types": "Business Types",
    "customer_types": "Customer Types",
    "loan_products": "Loan Products",
    "object_types": "Object Types",
    "industries": "Industries",
    "coverage_rules": "Coverage Rules",
    "excluded_regions": "Excluded Regions",
    "object_rule": "Object Rules",
    "max_ltv": "max. {value} % LTV",
    "max_commercial_area_share_percent": "Max. Commercial Area Share",
    "max_commercial_rent_share_percent": "Max. Commercial Rent Share",
    "max_leasehold_ltv_percent": "Max. Leasehold LTV",
    "minimum_energy_certificate_level": "Min. Energy Certificate Level",
    "accepts_weg": "Condominium (WEG) accepted",
    "accepts_leasehold_properties": "Leasehold accepted",
    "accepts_mining_damage_waiver": "Mining damage waiver accepted",
    "accepts_renovation_instead_of_min_energy_level": "Renovation instead of min. energy level",
    "accepts_pending_energy_certificate": "Pending energy certificate accepted",
    "businessType": {
      "buy_and_hold": "Buy & Hold",
      "commercial_trade": "Commercial Trade",
      "developer": "Developer",
      "subdivision": "Subdivision"
    },
    "customerType": {
      "civil_servant": "Civil Servant",
      "employee": "Employee",
      "entrepreneur": "Entrepreneur",
      "freelancer": "Freelancer",
      "rentier": "Rentier"
    },
    "loanProductType": {
      "annuity": "Annuity Loan",
      "building_society": "Building Society Loan",
      "bullet": "Bullet Loan",
      "non_recourse": "Non-Recourse",
      "subsidized": "Subsidized Loan",
      "variable": "Variable Loan"
    },
    "objectType": {
      "residential": "Residential",
      "mixed_use": "Mixed Use",
      "office": "Office",
      "commercial": "Commercial",
      "care_and_health": "Care & Health",
      "land": "Land",
      "hotel": "Hotel",
      "logistics": "Logistics",
      "wholesale_and_retail": "Wholesale & Retail",
      "other": "Other"
    },
    "borrowerKind": {
      "natural_person": "Natural Person",
      "legal_entity": "Legal Entity"
    },
    "coverageType": {
      "radius": "Radius",
      "state": "State",
      "nationwide": "Nationwide",
      "free_text": "Free Text"
    },
    "industryStatus": {
      "conditional": "Conditional",
      "excluded": "Excluded"
    }
  }
}
</i18n>
